import { Box, CircularProgress, Typography } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router'
import { toast } from 'react-toastify'
import { AppRoutes } from '../../Routes'
import { BreadcrumbNav } from '../../customComponents/BreadcrumbNav'
import { OrganizationCardList } from './cards/OrganizationCardList'
import { SetBreadcrumbContext } from '../../../contextProviders/BreadcrumbProvider'
import { UserInformationCard, type UserInformationFormValues } from './cards/UserInformationCard'
import { capabilitiesForMembership, isUserAdministrator } from './cards/cardPermissions'
import { GetOwnFunctionsForOrg } from '../../../services/AuthorizedFunctionService'
import { handleApiResponse } from '../../../utils/apiHelpers'
import { useAuth, type AuthUser } from '../../../hooks/use-auth'
import {
  GetUserDetails,
  SetOwnDefaultOrganization,
  UpdateOwnProfile,
  UploadOwnProfileImage
} from '../../../services/UserManagementService'
import type { User } from '../../../models/User'

const layout = {
  page: { display: 'flex', flexDirection: 'column', gap: '1em' },
  panels: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 500px) minmax(0, 1fr)' },
    gap: '24px',
    alignItems: 'start'
  }
}

export const UserProfile = (): JSX.Element => {
  const setBreadcrumbs = useContext(SetBreadcrumbContext)
  const { user } = useAuth()
  const [userDetails, setUserDetails] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setBreadcrumbs([{ name: 'Home', route: generatePath(AppRoutes.HOME) }])
  }, [])

  useEffect(() => {
    const loadUserDetails = async (user: AuthUser | null): Promise<void> => {
      if (user == null) return

      setIsLoading(true)

      const details = await handleApiResponse(GetUserDetails(user.id.toString()))
      if (details != null) setUserDetails(details)

      setIsLoading(false)
    }

    void loadUserDetails(user)
  }, [user])

  const handleDefaultOrgClicked = async (organizationId: number): Promise<void> => {
    if (userDetails == null) return

    const { success, errors } = await SetOwnDefaultOrganization(organizationId)
    if (!success) {
      errors.forEach((error) => { toast.error(error) })
      return
    }

    setUserDetails({
      ...userDetails,
      organizations: userDetails.organizations.map((org) => ({ ...org, isDefault: org.id === organizationId }))
    })
    toast.success('Default organization updated')
  }

  const handleSaveUserInformation = async (form: UserInformationFormValues): Promise<void> => {
    if (userDetails == null) return

    const { success, errors } = await UpdateOwnProfile({
      firstName: form.firstName,
      lastName: form.lastName,
      title: form.title,
      phoneNumber: form.phoneNumber,
      address: form.address
    })
    if (!success) {
      errors.forEach((error) => { toast.error(error) })
      return
    }

    setUserDetails({ ...userDetails, ...form, fullName: `${form.firstName} ${form.lastName}` })
    toast.success('User saved successfully')
  }

  const handleUploadImage = async (file: File): Promise<string | null> =>
    await handleApiResponse(UploadOwnProfileImage(file))

  // `userDetails.organizations[].userFunctions` cannot express Edit — see `heldFunctionRows`.
  const loadUserFunctions = async (orgId: number): Promise<string[] | null> =>
    userDetails == null ? null : await handleApiResponse(GetOwnFunctionsForOrg(orgId))

  return <Box data-testid='userProfile' sx={layout.page}>
    <BreadcrumbNav />

    <Typography variant='h1' data-testid='pageHeader'>User Profile</Typography>

    {userDetails != null && <Typography variant='h4' data-testid='profileSubheader'>{userDetails.fullName}</Typography>}

    {isLoading && <CircularProgress />}

    {!isLoading && userDetails != null && <Box sx={layout.panels}>
      <UserInformationCard
        user={userDetails}
        canEdit={true}
        allowEditEmail={false}
        onSave={handleSaveUserInformation}
        onUploadImage={handleUploadImage}
      />

      <OrganizationCardList
        organizations={userDetails.organizations}
        canSetDefault={true}
        capabilitiesFor={() => capabilitiesForMembership(true, isUserAdministrator(user), false)}
        onSetDefault={handleDefaultOrgClicked}
        loadUserFunctions={loadUserFunctions}
      />
    </Box>}
  </Box>
}
